import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    cleanClaim,
    normalizeTopic,
    selectDailyPosts,
    votePercentages
} from '../utils/truthpickContent';
import BrandPremise from '../components/public/BrandPremise';
import RevealPanel from '../components/public/RevealPanel';
import HomeSections, { HomeVideo } from '../components/public/HomeSections';
import { getHomeVideos } from '../api/videoApi';
import { getShowcaseItems } from '../api/photoApi';
import { HomePhoto } from '../components/public/HomeSections';
import './DailyTruthPage.css';

type VoteType = 'TRUTH' | 'LIE';

type Post = {
    id: number;
    content: string;
    category?: string | null;
    truthVoteCount?: number;
    lieVoteCount?: number;
    commentCount?: number;
};

const GUEST_PICK_KEY = 'truthpick_guest_picks';

type DailyTruthPageProps = {
    loadPostsRequest: () => Promise<Post[]>;
    voteRequest: (postId: number, voteType: VoteType) => Promise<unknown>;
    loadVideosRequest?: () => Promise<HomeVideo[]>;
    loadPhotosRequest?: () => Promise<HomePhoto[]>;
};

const DailyTruthPage = ({
    loadPostsRequest,
    voteRequest,
    loadVideosRequest = getHomeVideos,
    loadPhotosRequest = getShowcaseItems
}: DailyTruthPageProps) => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedVote, setSelectedVote] = useState<VoteType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [voting, setVoting] = useState(false);
    const [videos, setVideos] = useState<HomeVideo[]>([]);
    const [photos, setPhotos] = useState<HomePhoto[]>([]);

    const dailyPosts = useMemo(
        () => selectDailyPosts(posts, new Date(), 5),
        [posts]
    );
    const currentPost = dailyPosts[currentIndex];
    const token = localStorage.getItem('token');

    const loadPosts = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const data = await loadPostsRequest();
            setPosts(Array.isArray(data) ? data : []);
        } catch (requestError) {
            console.error(requestError);
            setError('오늘의 질문을 불러오지 못했어요.');
        } finally {
            setLoading(false);
        }
    }, [loadPostsRequest]);

    useEffect(() => {
        loadPosts();
    }, [loadPosts]);

    useEffect(() => {
        loadVideosRequest().then(setVideos).catch(() => setVideos([]));
    }, [loadVideosRequest]);

    useEffect(() => {
        loadPhotosRequest().then(setPhotos).catch(() => setPhotos([]));
    }, [loadPhotosRequest]);

    const handleVote = async (voteType: VoteType) => {
        if(!currentPost || voting || selectedVote) {
            return;
        }

        setVoting(true);
        setError('');

        try {
            if(token) {
                await voteRequest(currentPost.id, voteType);
            } else {
                const guestPicks = Number(localStorage.getItem(GUEST_PICK_KEY) || '0');
                localStorage.setItem(GUEST_PICK_KEY, String(guestPicks + 1));
            }

            setSelectedVote(voteType);
        } catch (requestError: any) {
            setError(
                requestError?.response?.data?.message
                || requestError?.response?.data
                || '투표를 저장하지 못했어요.'
            );
        } finally {
            setVoting(false);
        }
    };

    const showNext = () => {
        if(currentIndex < dailyPosts.length - 1) {
            setCurrentIndex((index) => index + 1);
            setSelectedVote(null);
            setError('');
        }
    };

    if(loading) {
        return (
            <main className="daily-page daily-state-page" aria-live="polite">
                <BrandPremise compact />
                <section className="daily-state">
                    <div className="daily-spinner" />
                    <p>오늘의 주장을 불러오는 중...</p>
                </section>
            </main>
        );
    }

    if(error && !currentPost) {
        return (
            <main className="daily-page daily-state-page">
                <BrandPremise compact />
                <section className="daily-state">
                    <h2>{error}</h2>
                    <button className="daily-primary-button" onClick={loadPosts}>다시 시도</button>
                </section>
            </main>
        );
    }

    if(!currentPost) {
        return (
            <main className="daily-page daily-state-page">
                <BrandPremise compact />
                <section className="daily-state">
                    <h2>새로운 질문을 준비하고 있어요.</h2>
                    <p>잠시 후 다시 들러주세요.</p>
                    <button className="daily-primary-button" onClick={loadPosts}>새로고침</button>
                </section>
            </main>
        );
    }

    const truthVotes = (currentPost.truthVoteCount || 0)
        + (selectedVote === 'TRUTH' ? 1 : 0);
    const lieVotes = (currentPost.lieVoteCount || 0)
        + (selectedVote === 'LIE' ? 1 : 0);
    const percentages = votePercentages(truthVotes, lieVotes);
    const guestPicks = Number(localStorage.getItem(GUEST_PICK_KEY) || '0');

    return (
        <main className="daily-page">
            <div className="daily-newsline"><div><span>TRUTHPICK JOURNAL</span><span>주장을 읽고, 근거를 묻고, 함께 판단합니다.</span></div><a href="#/board">모든 이야기 보기 ↗</a></div>
            <section className="daily-stage" aria-labelledby="truthpick-premise">
                <div className="daily-brand-column"><BrandPremise description="보이는 말보다, 그 아래의 근거를 먼저 봅니다." /><p className="daily-brand-intro">오늘의 질문에서 시작해 서로 다른 생각과 근거를 발견해 보세요.</p><div className="daily-brand-actions"><a href="#/mirror">가입 없이 이번 주 질문 고르기 ↗</a><a href="#/board">커뮤니티 둘러보기</a></div><div className="daily-brand-count"><strong>{posts.length.toLocaleString('ko-KR')}</strong><span>개의 이야기가 이어지고 있어요</span></div></div>

                <article className="daily-case" aria-live="polite" aria-labelledby="daily-title">
                    <header className="daily-case-meta">
                        <span className="daily-topic">{normalizeTopic(currentPost.category)}</span>
                        <span>{currentIndex + 1} / {dailyPosts.length}</span>
                    </header>
                    <p className="daily-case-label">오늘 덮여 있는 주장</p>
                    <h2 id="daily-title">{cleanClaim(currentPost.content, 68)}</h2>
                    <p className="daily-prompt">이 주장은 진실일까요, 거짓일까요?</p>
                    <a className="daily-full-story" href={`#/board?post=${currentPost.id}`}>이야기 전체 보기 ↗</a>

                    <div className="daily-decision" role="group" aria-label="진실 판정">
                        {!selectedVote ? (
                            <>
                                <div className="daily-vote-grid">
                                    <button
                                        type="button"
                                        className="daily-vote truth"
                                        aria-label="TRUE · 진실"
                                        disabled={voting}
                                        onClick={() => handleVote('TRUTH')}
                                    >
                                        <small>진실이라고 판단</small>
                                        진실
                                    </button>
                                    <button
                                        type="button"
                                        className="daily-vote lie"
                                        aria-label="FALSE · 거짓"
                                        disabled={voting}
                                        onClick={() => handleVote('LIE')}
                                    >
                                        <small>거짓이라고 판단</small>
                                        거짓
                                    </button>
                                </div>
                                <div className="daily-result-lock">
                                    <RevealPanel concealed label="판단 전에는 보이지 않습니다.">
                                        <div className="daily-result-placeholder" />
                                    </RevealPanel>
                                </div>
                            </>
                        ) : (
                            <RevealPanel concealed={false} label="">
                                <div className="daily-result">
                                    <div className="daily-result-title">
                                        <span aria-hidden="true">✓</span>
                                        <strong>
                                            {selectedVote === 'TRUTH' ? '진실을 선택했습니다.' : '거짓을 선택했습니다.'}
                                        </strong>
                                    </div>
                                    <p className="daily-explanation">
                                        이 주장의 배경과 근거는 작성 내용과 출처를 기준으로 확인해야 합니다.
                                        현재 게시물에는 별도 출처 정보가 등록되어 있지 않아요.
                                    </p>
                                    <div className="daily-community">
                                        <p>사람들의 선택</p>
                                        <div className="daily-result-counts">
                                            <div><strong>{percentages.truth}%</strong><span>진실 · {truthVotes}</span></div>
                                            <div><strong>{percentages.lie}%</strong><span>거짓 · {lieVotes}</span></div>
                                        </div>
                                    </div>
                                    {!token && (
                                        <div className="daily-guest-note">
                                            <strong>체험 투표라서 서버에는 기록되지 않아요.</strong>
                                            <span>{guestPicks}/3회 체험 · 로그인하면 활동을 저장할 수 있어요.</span>
                                            {guestPicks >= 3 && <a href="#/login">로그인하고 계속하기</a>}
                                        </div>
                                    )}
                                    {currentIndex < dailyPosts.length - 1 ? (
                                        <button type="button" className="daily-primary-button" onClick={showNext}>다음 주장</button>
                                    ) : (
                                        <a className="daily-primary-link" href="#/board">더 많은 주장 살펴보기</a>
                                    )}
                                </div>
                            </RevealPanel>
                        )}
                        {error && <p className="daily-error" role="alert">{error}</p>}
                    </div>
                </article>
                <aside className="daily-live-rail" aria-label="커뮤니티 소식"><div className="daily-rail-eyebrow">TODAY'S COMMUNITY</div><h2>함께 보고<br />이야기해요.</h2><p>당신의 판단을 기다리는 다양한 주장이 있습니다.</p><div className="daily-rail-divider" /><strong>지금 눈여겨볼 주제</strong>{Array.from(new Set(posts.map(post => post.category).filter(Boolean))).slice(0, 5).map(category => <a key={category} href={`#/board?tab=CATEGORY&category=${encodeURIComponent(category || '')}`}><span>{category}</span><span aria-hidden="true">↗</span></a>)}<a className="daily-rail-all" href="#/board">전체 주제 살펴보기 ↗</a></aside>
            </section>
            <HomeSections posts={posts} videos={videos} photos={photos} />
        </main>
    );
};

export default DailyTruthPage;
